import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { FaBan, FaCheck, FaClock, FaExclamationTriangle, FaHourglassHalf, FaIndustry, FaListUl, FaPlaneDeparture, FaRegCalendarCheck, FaDownload, FaSearch, FaExternalLinkAlt } from 'react-icons/fa';
import { getTravelDashboard, getTravelDrilldown, exportTravelDrilldown, getTravelDashboardTransactions } from 'redux/booking/dashboard/travelSlice';
import { exportBookings } from 'redux/booking/travel/travelBookingSlice';
import { debounce } from 'helpers/debounce';
import { ReportDateRangePicker, LoadingSpinner, Button, AgGridTable, Pagination } from 'components/common';
import { StatTile, BarList, DonutChart, YoYChart, BookingList, SiteFilter } from 'components/app/bookings/dashboard/DashboardBits';
import DrilldownDrawer from 'components/app/bookings/dashboard/DrilldownDrawer';
import TravelCalendar from 'components/app/bookings/travel/TravelCalendar';

const LINK_BASE = '/booking/travel';
const fmtDate = (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '');

// Trip-ID cell → opens the trip detail in a new tab.
const TripLinkRenderer = (p) => (
  <Link to={`${LINK_BASE}/${p.data?.id}`} target="_blank" className="tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center tw-gap-1">
    {p.value} <FaExternalLinkAlt size={8} className="tw-opacity-60" />
  </Link>
);

// Full travel-booking-transaction grid columns (Trip ID pinned left).
const TXN_COLUMN_DEFS = [
  { headerName: 'Trip ID', field: 'booking_number', pinned: 'left', minWidth: 140, cellRenderer: TripLinkRenderer },
  { headerName: 'Trip name', field: 'title', minWidth: 200 },
  { headerName: 'Destination', field: 'destination', minWidth: 200 },
  { headerName: 'Purpose', field: 'purpose_of_travel', minWidth: 170 },
  { headerName: 'Travel date', field: 'date_of_travel', minWidth: 130, valueFormatter: (p) => fmtDate(p.value) },
  { headerName: 'Site', field: 'site_name', minWidth: 130 },
  { headerName: 'Submitted by', field: 'submited_by', minWidth: 130 },
  { headerName: 'Submitted date', field: 'submited_date', minWidth: 150, valueFormatter: (p) => fmtDate(p.value) },
  { headerName: 'Status', field: 'status_name', minWidth: 150 },
];

// Drill-down detail columns (Trip ID pinned left).
const DRILL_COLUMN_DEFS = [
  { headerName: 'Trip ID', field: 'booking_number', pinned: 'left', minWidth: 100, width: 120, flex: 0, cellRenderer: TripLinkRenderer },
  { headerName: 'Trip name', field: 'title', minWidth: 200 },
  { headerName: 'Destination', field: 'destination', minWidth: 200 },
  { headerName: 'Purpose', field: 'purpose_of_travel', minWidth: 170 },
  { headerName: 'Date', field: 'date_of_travel', minWidth: 130, valueFormatter: (p) => fmtDate(p.value) },
  { headerName: 'Status', field: 'status_name', minWidth: 150 },
];

const TravelBookingDashboardPage = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((s) => s.booking_dashboard_travel);
  const [range, setRange] = useState({ startDate: dayjs().startOf('year').format('YYYY-MM-DD'), endDate: dayjs().endOf('year').format('YYYY-MM-DD') });
  const [siteIds, setSiteIds] = useState(null); // null = not yet seeded
  const [drill, setDrill] = useState({ open: false, item: null });

  const {
    stats = {}, yoy = {}, sites = [], by_status = [], by_site = [], by_purpose = [], by_type = [],
    customer_visit_factory = {}, cvf_this_week = [], cvf_next_week = [], this_week = [], upcoming = [], urgent = [],
    stale_pending = [],
  } = data || {};

  const siteParam = () => {
    const v = siteIds || [];
    return v.length && sites.length && v.length < sites.length ? { site_id: v } : {};
  };
  const params = { from_date: range.startDate, end_date: range.endDate, ...siteParam() };

  useEffect(() => {
    dispatch(getTravelDashboard({ from_date: range.startDate, end_date: range.endDate, ...siteParam() }));
    // siteParam reads `sites` from the response itself; depending on it would refetch on every response — siteIds covers the user's selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, range, siteIds]);

  useEffect(() => {
    if (siteIds === null && sites.length) setSiteIds(sites.map((s) => s.value));
  }, [sites, siteIds]);

  const openDrill = useCallback((item) => setDrill({ open: true, item }), []);
  const openStat = (key, label, value) => openDrill({ dimension: 'stat', key, label, value });

  // ── Server-side paginated travel-booking transactions grid ──────────────
  const [txnSearch, setTxnSearch] = useState('');
  const [txn, setTxn] = useState({ rows: [], total: 0, page: 1, pageSize: 20, loading: false });
  const searchRef = useRef('');

  const fetchTransactions = useCallback((page = 1, pageSize = 20) => {
    const sp = siteParam();
    setTxn((t) => ({ ...t, loading: true }));
    dispatch(getTravelDashboardTransactions({
      from_date: range.startDate,
      end_date: range.endDate,
      ...(sp.site_id ? { site_id: sp.site_id } : {}),
      page,
      per_page: pageSize,
      ...(searchRef.current ? { search: searchRef.current } : {}),
    })).then((res) => {
      const p = res.payload || {};
      setTxn({ rows: p.rows || [], total: p.total_rows || 0, page, pageSize, loading: false });
    });
    // siteParam reads `sites` from the response itself; depending on it would reload the grid on every dashboard response — siteIds covers the user's selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, range, siteIds]);

  // (Re)load page 1 whenever the date range / site scope changes.
  useEffect(() => { fetchTransactions(1, 20); }, [fetchTransactions]);

  const fetchTxnRef = useRef(fetchTransactions);
  fetchTxnRef.current = fetchTransactions;
  const debouncedTxnSearch = useRef(debounce(() => fetchTxnRef.current(1, 20), 500)).current;
  const handleTxnSearch = (e) => { const v = e.target.value; setTxnSearch(v); searchRef.current = v; debouncedTxnSearch(); };

  // Server-side Excel export of ALL trips in the current dashboard range/site scope + search.
  const exporting = useSelector((s) => s.booking_travel?.exporting);
  const handleExportTransactions = () => {
    const sp = siteParam();
    dispatch(exportBookings({
      date_of_travel_gteq: range.startDate,
      date_of_travel_lteq: range.endDate,
      ...(sp.site_id ? { site_id_in: sp.site_id } : {}),
      ...(searchRef.current ? { id_or_trip_name_or_destination_or_job_number_cont: searchRef.current } : {}),
    }));
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaPlaneDeparture className="tw-text-blue-600" size={20} />
          <div>
            <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Travel Booking Dashboard</h1>
            <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Trips by status, purpose, type, site & upcoming — click any bar for details.</p>
          </div>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg tw-p-3 tw-shadow-sm">
        <ReportDateRangePicker startDate={range.startDate} endDate={range.endDate} onChange={setRange} />
        <SiteFilter sites={sites} value={siteIds || []} onChange={setSiteIds} />
        {loading && <LoadingSpinner />}
      </div>

      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-3">
        <StatTile label="Total trips" value={stats.total} icon={FaListUl} delta={yoy.delta_pct} onClick={() => openStat('total', 'Total trips', stats.total)} />
        <StatTile label="Pending approval" value={stats.pending} icon={FaClock} accent="tw-text-amber-600" bg="tw-bg-amber-50" onClick={() => openStat('pending', 'Pending approval', stats.pending)} />
        <StatTile label="Approved" value={stats.approved} icon={FaCheck} accent="tw-text-green-600" bg="tw-bg-green-50" onClick={() => openStat('approved', 'Approved', stats.approved)} />
        <StatTile label="Canceled" value={stats.canceled} icon={FaBan} accent="tw-text-red-600" bg="tw-bg-red-50" onClick={() => openStat('canceled', 'Canceled', stats.canceled)} />
        <StatTile label="This week" value={stats.this_week} icon={FaRegCalendarCheck} accent="tw-text-primary-600" bg="tw-bg-primary-50" onClick={() => openStat('this_week', 'This week', stats.this_week)} />
        <StatTile label="Urgent to approve" value={stats.urgent} icon={FaExclamationTriangle} accent="tw-text-red-600" bg="tw-bg-red-50" highlight={stats.urgent > 0} onClick={() => openStat('urgent', 'Urgent to approve', stats.urgent)} />
      </div>

      {(cvf_this_week.length > 0 || cvf_next_week.length > 0) && (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
          <BookingList title="Customer visit factory — this week" items={cvf_this_week} dateField="date_of_travel" metaField="destination" variant="highlight" icon={FaIndustry} linkBase={LINK_BASE} emptyText="No CVF trips this week" />
          <BookingList title="Customer visit factory — next week" items={cvf_next_week} dateField="date_of_travel" metaField="destination" variant="highlight" icon={FaIndustry} linkBase={LINK_BASE} emptyText="No CVF trips next week" />
        </div>
      )}

      {(urgent.length > 0 || stale_pending.length > 0) && (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
          <BookingList title="Urgent — departing soon, not yet approved" items={urgent} dateField="date_of_travel" metaField="destination" variant="urgent" icon={FaExclamationTriangle} linkBase={LINK_BASE} emptyText="No urgent trips" />
          <BookingList title="Long pending / missed" items={stale_pending} dateField="date_of_travel" metaField="destination" variant="highlight" icon={FaHourglassHalf} linkBase={LINK_BASE} emptyText="Nothing overdue" />
        </div>
      )}

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <DonutChart title="By status" items={by_status} onItemClick={openDrill} />
        <BarList title="By site office" items={by_site} onItemClick={openDrill} />
      </div>
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <DonutChart title="By purpose of travel" items={by_purpose} onItemClick={openDrill} />
        <DonutChart title="By type of travel" items={by_type} onItemClick={openDrill} />
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <BarList title={`Customer visit factory — by destination (${customer_visit_factory.count ?? 0} total)`} items={customer_visit_factory.by_destination || []} onItemClick={openDrill} emptyText="No customer-visit-factory trips" />
        <BookingList title="Customer visit factory — trips" items={customer_visit_factory.trips || []} dateField="date_of_travel" metaField="destination" icon={FaIndustry} linkBase={LINK_BASE} emptyText="None" />
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
        <YoYChart title="This year vs last year" yoy={yoy} />
        <BookingList title="This week" items={this_week} dateField="date_of_travel" metaField="destination" linkBase={LINK_BASE} emptyText="No trips this week" />
      </div>

      <BookingList title="Upcoming (30 days)" items={upcoming} dateField="date_of_travel" metaField="destination" linkBase={LINK_BASE} emptyText="No upcoming trips" />

      {/* Travel booking transactions — full detail grid (AgGridTable): quick search,
          Trip ID pinned left, server-side Excel export of the full range. */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mt-2">
        <div className="tw-flex tw-items-center tw-gap-2">
          <FaListUl className="tw-text-blue-600" size={16} />
          <h2 className="tw-mb-0 tw-font-bold tw-text-lg tw-text-gray-900">Travel booking transactions</h2>
          <span className="tw-text-slate-400 tw-text-sm">{txn.total} transaction(s)</span>
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-relative tw-flex tw-items-center tw-w-64">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input type="text" value={txnSearch} onChange={handleTxnSearch} placeholder="Search trip, destination, submitter..."
              className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-sm tw-border tw-border-solid tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 tw-bg-white tw-text-slate-700" />
          </div>
          <Button variant="secondary" size="sm" leftIcon={FaDownload} loading={exporting} onClick={handleExportTransactions}>Export Excel</Button>
        </div>
      </div>
      <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
        <AgGridTable rowData={txn.rows} columnDefs={TXN_COLUMN_DEFS} pagination={false} filter={false} height="55vh" loading={txn.loading} />
        <Pagination
          currentPage={txn.page}
          totalPages={Math.max(1, Math.ceil(txn.total / txn.pageSize))}
          totalResults={txn.total}
          perPage={txn.pageSize}
          onPageChange={(p) => fetchTransactions(p, txn.pageSize)}
          onPageSizeChange={(sz) => fetchTransactions(1, sz)}
          pageSizeOptions={[20, 50, 100]}
        />
      </div>

      {/* Booking calendar, centralized in the dashboard */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mt-2">
        <FaRegCalendarCheck className="tw-text-blue-600" size={16} />
        <h2 className="tw-mb-0 tw-font-bold tw-text-lg tw-text-gray-900">Travel calendar</h2>
      </div>
      <div className="-tw-mt-1"><TravelCalendar /></div>

      <DrilldownDrawer
        show={drill.open}
        onClose={() => setDrill({ open: false, item: null })}
        item={drill.item}
        baseParams={params}
        fetchThunk={getTravelDrilldown}
        exportThunk={exportTravelDrilldown}
        columnDefs={DRILL_COLUMN_DEFS}
        unit="trip"
      />
    </div>
  );
};

export default TravelBookingDashboardPage;
